import { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { Button, ButtonSize, ButtonThemes, FormField, MiniLoader } from '@app/shared';
import { yupResolver } from '@hookform/resolvers/yup';
import { schemaFormFeedBackLk } from '@helpers/validationForm';
import { fetchFeedBackLk } from '../../module/services/fetchFeedBackLk';
import { useAppSelector } from '@redux/hooks';
import { getAuthUserData } from '@entities/Auth';
import IconSuccess from '../icons/success.svg';
import IconError from '../icons/error.svg';
import IconEmoji1 from '../icons/emoji-1.svg';
import IconEmoji2 from '../icons/emoji-2.svg';
import IconEmoji3 from '../icons/emoji-3.svg';
import IconEmoji4 from '../icons/emoji-4.svg';
import IconEmoji5 from '../icons/emoji-5.svg';

import classNames from 'classnames';

import styles from './feedBackLk.module.scss';

type FormFieldsTyp = {
  rating: number;
  message: string;
};

export const FeedBackLk = ({ className }: { className?: string }) => {
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const { userPhone } = useAppSelector(getAuthUserData);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<FormFieldsTyp>({
    resolver: yupResolver(schemaFormFeedBackLk),
    mode: 'onChange',
    defaultValues: {
      message: '',
      rating: undefined,
    },
  });

  const watchAllFields = watch();

  const onSubmit = async (data: FormFieldsTyp) => {
    setIsFetching(true);
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const pageUrl = window?.location?.href || '';
    const captchaTokenGoogle = await executeRecaptcha('submit');
    const response = await fetchFeedBackLk({
      ...data,
      urlPage: pageUrl,
      captchaTokenGoogle,
      phone: userPhone || null,
    });
    if (response) {
      setSuccess(true);
      setError(false);
    } else {
      setError(true);
    }

    setIsFetching(false);
  };

  useEffect(() => {
    const timer =
      success &&
      setInterval(() => {
        setSuccess(false);
        reset();
      }, 3000);
    return () => clearInterval(timer);
  }, [success]);

  if (error) {
    return (
      <div className={classNames(styles.Wrapper, styles.Error, className)} data-testid="wrapper-feedback-lk">
        <div className={styles.Title}>Cервис временно не&#160;работает</div>
        <IconError width={40} height={40} />
        <div className={styles.Content}>Скоро всё исправим!</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={classNames(styles.Wrapper, styles.Success, className)} data-testid="wrapper-feedback-lk">
        <div className={styles.Title}>Спасибо</div>
        <IconSuccess width={40} height={40} />
        <div className={styles.Content}>Вы помогаете нам стать лучше</div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.Wrapper, className)} data-testid="wrapper-feedback-lk">
      <div className={styles.Title}>Насколько удобен личный кабинет?</div>
      <FeedBackLk.Rating rating={watchAllFields.rating} setValue={setValue} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormField
          classNameContainer={styles.TextArea}
          name={'message'}
          placeholder={'Ваш комментарий'}
          type={'textarea'}
          register={register}
          value={watchAllFields.message}
          setValue={setValue}
          error={errors?.message?.message}
          limit={300}
          isValid={Boolean(!errors?.message?.message) && Boolean(watchAllFields.message)}
          dataTestId="message-feedback-lk"
        />
        <Button
          className={classNames({
            [styles.Success]: success,
            [styles.IsFetching]: isFetching,
          })}
          size={ButtonSize.S}
          disabled={isFetching}
          themes={success ? ButtonThemes.Success : ButtonThemes.DarkViolet}
        >
          {success ? 'Отправлено' : 'Ответить'}
          {isFetching && <MiniLoader className={styles.Loader} />}
        </Button>
      </form>
    </div>
  );
};

const EmojiArray = [<IconEmoji1 key={1} />, <IconEmoji2 key={2} />, <IconEmoji3 key={3} />, <IconEmoji4 key={4} />, <IconEmoji5 key={5} />];

FeedBackLk.Rating = function Rating({ rating, setValue }) {
  return (
    <div className={styles.RowEmoji}>
      {Array.from(Array(5).keys()).map(item => {
        return (
          <label
            key={item}
            htmlFor={`rating_lk_${item + 1}`}
            className={classNames(styles.RatingItem, { [styles.Active]: rating === item + 1 })}
          >
            {EmojiArray[item]}
            <input
              onChange={e => setValue('rating', parseInt(e.target.value))}
              id={`rating_lk_${item + 1}`}
              type="radio"
              name={'rating_lk'}
              value={item + 1}
            />
          </label>
        );
      })}
    </div>
  );
};
